import type { CascadeOption } from '@byecode/ui'
import type { AnyObject, Pagination } from '@lighthouse/core'
import {
    type Conditions,
    type FieldCellValue,
    type FieldInputConfigProtocol,
    type FilterFormType,
    type PageStackBlockValueBlockMap,
    type PaginationProtocol,
    type RecordLikeProtocol,
    type SortableProtocol
} from '@lighthouse/core'
import type { RelativeDataSourceConfigItem, UserOption } from '@lighthouse/shared'
import {
    CURRENT_USER,
    getCascadeOption,
    getFilterOfDeepBlockIds,
    getWithScopeId,
    getWithScopeIdAndRecordIdAndColumnId,
    isNumberValue,
    isStringArray,
    pageStackPubSub,
    recTakeFilterVariable,
    resolveFilter,
    useHandleAbortPrevious,
    USER_DATASOURCE
} from '@lighthouse/shared'
import { useSetAtom } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import { find, reduce } from 'rambda'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useUpdateEffect } from 'react-use'
import useSWRMutation from 'swr/mutation'
import { debounce } from 'throttle-debounce'

import { addRecordsAtom } from '@/atoms/dataSource/action'
import { viewIndependentDataAtomFamily } from '@/atoms/dataSource/state'
import { useCurrentStackIdContext } from '@/contexts/PageContext'
import * as srv from '@/services'
import type { DataSourceAndRecordsResult } from '@/services/types'

import { useCurrentAppId, useCurrentEnvId } from './useApplication'
import { useDataSourceList } from './useDataSource'
import { useParseVariableWithSubscription } from './useParseVariable'

type DataSourcePayload = {
    pagination: PaginationProtocol['pagination']
    search?: string
    dsId: string
    filter?: FilterFormType
    disabledFilter?: FilterFormType
    abort?: AbortController
    blockId?: string
    scope?: string
} & SortableProtocol

type DataSourceOptionPayload = {
    dsId: string
    filter?: FilterFormType
    fieldId: string
    showFieldId: string
    disabledFilter?: FilterFormType
    isDebounce?: boolean
    id: string
} & SortableProtocol

export type FieldInputConfigItem = {
    config: FieldInputConfigProtocol
    value?: FieldCellValue
    blockId: string
    columnId?: string
    parentRecordId?: string
    scope?: string
}

// type PickDataForRelativeSelectConfigItem = Pick<RelativeSelectConfig['relativeSelect'], 'filter' | 'disabledFilter' | 'sorts' | 'relativePointer' | 'relativeFieldPointer' | 'relativeShowFieldPointer'>

/** 获取关联数据源数据 */
// TODO: 后端不返回分页数据 请从参数中拿取
export function useFieldBlockMethods(fieldBlocks?: FieldInputConfigItem[], fieldBlockValueMap?: PageStackBlockValueBlockMap) {
    const initLoadRef = useRef(false)

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const stackId = useCurrentStackIdContext()
    const dataSourceList = useDataSourceList(appId, envId)
    // Atom setters for managing records
    const addRecords = useSetAtom(addRecordsAtom)

    const { configList: newConfigList, blockIds } = useMemo(() => {
        return reduce<FieldInputConfigItem, { configList: RelativeDataSourceConfigItem[]; blockIds: string[] }>(
            ({ configList, blockIds }, item) => {
                const { config, value, blockId, scope, parentRecordId, columnId } = item
                if (config.inputType === 'relativeSelect') {
                    const { relativeSelect } = config
                    const newFilterBlockIds = getFilterOfDeepBlockIds(relativeSelect?.filter)
                    const newDisabledFilterBlockIds = getFilterOfDeepBlockIds(relativeSelect?.disabledFilter)
                    const newBlockIds = new Set([...blockIds, ...newFilterBlockIds, ...newDisabledFilterBlockIds])
                    const id = getWithScopeIdAndRecordIdAndColumnId(blockId, scope, parentRecordId, columnId)
                    const blockKey = getWithScopeId(blockId, scope)
                    const newConfig = { config, value, id }
                    return { configList: [...configList, newConfig], blockIds: [...newBlockIds, blockKey].map(String) }
                }
                return { configList, blockIds }
            },
            { configList: [], blockIds: [] },
            fieldBlocks ?? []
        )
    }, [fieldBlocks])

    // const [isInit, setIsInit] = useState(newConfigList.filter(item => item.value).length === 0)

    // const usedFieldInputValueMap = useMemo(() => {
    //     const newFieldInputValueMap = pick(blockIds, fieldBlockValueMap) || {}
    //     if (equal(prevFieldBlockValueRef.current, newFieldInputValueMap)) {
    //         return prevFieldBlockValueRef.current
    //     }
    //     prevFieldBlockValueRef.current = newFieldInputValueMap
    //     return newFieldInputValueMap
    // }, [blockIds, fieldBlockValueMap])

    const relativeSelectList = useMemo(() => newConfigList.map(item => item.config.relativeSelect), [newConfigList])

    const handleChange = useAtomCallback(
        useCallback(
            (get, set, params: { id: string; data: DataSourceAndRecordsResult; pagination?: Pagination & { search?: string } }) => {
                const { id, data, pagination } = params
                const { datasource, records = [] } = data ?? {}
                if (!datasource) {
                    return
                }
                const { id: dsId } = datasource
                set(viewIndependentDataAtomFamily({ stackId, dsId, viewId: id }), draft => {
                    draft.recordPoints = records.map(r => ({ id: r.id, disabled: r?.disabled }))
                    if (pagination) {
                        draft.currentPage = pagination?.currentPage ?? 1
                        draft.rowTotal = pagination?.rowTotal ?? records.length
                        draft.search = pagination?.search ?? ''
                    }
                })
                addRecords({ records })
            },
            [addRecords, stackId]
        )
    )

    const { trigger: getInitRecords } = useSWRMutation(
        `getDs-init-records`,
        async (
            _,
            {
                arg
            }: {
                arg: DataSourcePayload & {
                    id: string
                }
            }
        ) => {
            const { pagination, search, dsId, filter, sorts } = arg
            const data = await srv.getDs({
                appId,
                envId,
                dsId,
                pagination,
                search,
                filter,
                sorts
            })

            if (data) {
                addRecords({ records: data.records || [] })
            }
            return data
        }
    )

    const { trigger: getDsSwr } = useSWRMutation(`getDs-fetch`, async (_, { arg }: { arg: DataSourcePayload & { id: string } }) => {
        const { id, pagination, search, dsId, filter, sorts, abort, disabledFilter } = arg
        const data = await srv.getDs({
            appId,
            envId,
            dsId,
            pagination,
            search,
            filter,
            sorts,
            disabledFilter,
            abort
        })
        const total = await srv.getDsCount({
            envId,
            dsId,
            search,
            filter,
            abort
        })
        if (!data) {
            return
        }
        handleChange({
            id,
            data,
            pagination: {
                currentPage: pagination.currentPage,
                rowTotal: total,
                search: search ?? ''
            }
        })
    })
    const { mutation: updateDataSource } = useHandleAbortPrevious(getDsSwr)

    const handleFetchRelativeOptions = useAtomCallback(
        useCallback(
            async (get, set, _: string, { arg }: { arg: DataSourceOptionPayload & { id: string } }) => {
                const { id, fieldId, dsId, filter, sorts, showFieldId, disabledFilter } = arg
                const data = await srv.getRelativeOptions({
                    envId,
                    dsId,
                    fieldId,
                    filter,
                    sorts,
                    disabledFilter,
                    showFieldId
                })

                // 补充 appId/envId/dsId 并存入 recordPoolAtom
                const fullRecords: RecordLikeProtocol[] = data.map(record => ({
                    ...record,
                    appId,
                    envId,
                    dsId
                }))

                // 使用 addRecordsAtom 添加/更新 records
                addRecords({ records: fullRecords })
                set(viewIndependentDataAtomFamily({ stackId, dsId, viewId: id }), draft => {
                    draft.recordPoints = fullRecords.map(r => ({ id: r.id }))
                    draft.currentPage = 1
                    draft.rowTotal = fullRecords.length
                })
            },
            [addRecords, appId, envId, stackId]
        )
    )
    const { trigger: getDsOption } = useSWRMutation(`getDs-options`, handleFetchRelativeOptions)
    const debounceGetDsOption = useMemo(() => debounce(1000, getDsOption), [getDsOption])

    const handleFetchLoadMoreData = useAtomCallback(
        useCallback(
            async (get, set, _: string, { arg }: { arg: DataSourcePayload & { id: string } }) => {
                const { id, pagination, search, dsId, filter, sorts } = arg
                const dataSource = await srv.getDs({
                    appId,
                    envId,
                    dsId,
                    pagination,
                    search,
                    filter,
                    sorts
                })
                if (!dataSource) {
                    return
                }

                // 添加 records 到 recordPoolAtom
                const recordIds = dataSource.records.map(r => ({ id: r.id, disabled: r?.disabled }))
                addRecords({ records: dataSource.records })
                set(viewIndependentDataAtomFamily({ stackId, dsId, viewId: id }), draft => {
                    draft.recordPoints = [...draft.recordPoints, ...recordIds]
                    draft.currentPage = pagination?.currentPage ?? 1
                })
            },
            [addRecords, appId, envId, stackId]
        )
    )
    const { trigger: onLoadMoreData } = useSWRMutation(`getDs-loadMore`, handleFetchLoadMoreData)

    const fieldFilter = useMemo(() => {
        return fieldBlocks?.reduce<FilterFormType[]>((prev, next) => {
            if (next.config.inputType === 'cascade' && next.config.cascade?.filter) {
                return [...prev, next.config.cascade.filter]
            }

            if (next.config.inputType === 'person' && next.config.person?.filter) {
                return [...prev, next.config.person.filter]
            }

            if (next.config.inputType === 'relativeSelect') {
                if (next.config.relativeSelect?.filter) {
                    prev.push(next.config.relativeSelect.filter)
                }
                if (next.config.relativeSelect?.disabledFilter) {
                    prev.push(next.config.relativeSelect.disabledFilter)
                }
            }

            return prev
        }, [])
    }, [fieldBlocks])
    const fieldVariables = useMemo(
        () => fieldFilter?.flatMap(item => recTakeFilterVariable(item?.expression)).filter(Boolean),
        [fieldFilter]
    )
    const parseVariable2Field = useParseVariableWithSubscription(fieldVariables)
    const handleFetchDataDataSource = useCallback(
        (params: Omit<DataSourcePayload, 'appId'> & { id: string }) => {
            const { dsId } = params
            const relativeSelect = find(item => item?.relativePointer === dsId, relativeSelectList)
            if (!dsId || !relativeSelect) {
                return Promise.resolve(undefined)
            }
            const { filter, sorts, disabledFilter } = relativeSelect
            return updateDataSource({
                sorts,
                ...params,
                filter: resolveFilter({
                    filter,
                    extraParams: {
                        dataSourceList,
                        parseVariable2Field
                    }
                }),
                disabledFilter: resolveFilter({
                    filter: disabledFilter,
                    extraParams: {
                        dataSourceList,
                        parseVariable2Field
                    }
                })
            })
        },
        [relativeSelectList, updateDataSource, dataSourceList, parseVariable2Field]
    )

    const handleLoadMoreData = useCallback(
        (params: Omit<DataSourcePayload, 'appId'> & { id: string }) => {
            const { dsId } = params
            const relativeSelect = find(item => item.config.relativeSelect?.relativePointer === dsId, newConfigList)?.config.relativeSelect
            if (!dsId || !relativeSelect) {
                return Promise.resolve(undefined)
            }
            const { filter, sorts, disabledFilter } = relativeSelect
            return onLoadMoreData({
                sorts,
                ...params,
                filter: resolveFilter({
                    filter,
                    extraParams: {
                        dataSourceList,
                        parseVariable2Field
                    }
                }),
                disabledFilter: resolveFilter({
                    filter: disabledFilter,
                    extraParams: {
                        dataSourceList,
                        parseVariable2Field
                    }
                })
            })
        },
        [newConfigList, onLoadMoreData, dataSourceList, parseVariable2Field]
    )

    const handleFetchDataSourceOptions = useCallback(
        (params: DataSourceOptionPayload & { id: string }) => {
            const { dsId, isDebounce } = params
            const relativeSelect = find(item => item.config.relativeSelect?.relativePointer === dsId, newConfigList)?.config.relativeSelect
            if (!dsId || !relativeSelect) {
                return undefined
            }
            initLoadRef.current = true
            return isDebounce ? debounceGetDsOption(params) : getDsOption(params)
        },
        [debounceGetDsOption, getDsOption, newConfigList]
    )
    const handleFetchCascadeOptions = useCallback(
        async (params: {
            dsId: string
            sortFieldPointer: string
            fieldPointer: string
            showFieldPointer: string
            filter?: FilterFormType | undefined
            parentFieldPointer: string
            disableId?: string
        }): Promise<CascadeOption[] | undefined> => {
            const { dsId, parentFieldPointer, fieldPointer, sortFieldPointer, filter, showFieldPointer, disableId } = params
            const isEmptyConfig = !dsId || !parentFieldPointer || !fieldPointer
            if (isEmptyConfig) {
                return
            }
            const res = await srv.getDs({
                appId,
                envId,
                dsId,
                filter: resolveFilter({
                    filter,
                    extraParams: {
                        dataSourceList,
                        parseVariable2Field
                    }
                }),
                pagination: { currentPage: 1, pageSize: 5000 },
                sorts: [{ id: 'cascadeSortId', order: 'ASC', fieldId: sortFieldPointer }]
            })
            const { records = [] } = res ?? {}
            addRecords({ records })
            const usedRecords = records?.filter(record => record?.content?.[fieldPointer]?.value)
            // setBlockRecord({
            //     identifier: { dsId, viewId: id },
            //     data: draft => {
            //         draft.recordIds = usedRecords.map(r => r.id)
            //         draft.currentPage = 1
            //         draft.rowTotal = usedRecords.length
            //     }
            // })

            return (
                usedRecords
                    ?.filter(record => {
                        const parentId = record?.content?.[parentFieldPointer]?.value
                        return isStringArray(parentId) ? !parentId?.[0] : !parentId
                    })
                    .map(record => getCascadeOption({ parentFieldPointer, fieldPointer, showFieldPointer, record, records, disableId }))
                    .filter(item => item.value) ?? []
            )
        },
        [addRecords, appId, dataSourceList, envId, parseVariable2Field]
    )

    const handleFetchPersonOptions = useCallback(
        async (filter: FilterFormType): Promise<UserOption[]> => {
            const res = await srv.getDs({
                appId,
                envId,
                dsId: USER_DATASOURCE,
                filter: resolveFilter({
                    filter,
                    extraParams: {
                        dataSourceList,
                        parseVariable2Field
                    }
                }),
                pagination: { currentPage: 1, pageSize: 30000 }
            })
            if (res) {
                const { records } = res
                return records.map(r => {
                    const { content } = r
                    return {
                        id: content['ID']?.value?.toString() ?? '',
                        name: content['USERNAME']?.value?.toString() ?? '',
                        avatar: isStringArray(content['AVATAR']?.value) ? content['AVATAR'].value[0] : undefined,
                        status: isStringArray(content['STATUS']?.value) ? content['STATUS'].value[0] : undefined
                    }
                })
            }
            return []
        },
        [appId, dataSourceList, envId, parseVariable2Field]
    )

    const handleGetInitRecord = useCallback(
        async (params: RelativeDataSourceConfigItem) => {
            const {
                id,
                config: { relativeSelect, title },
                value
            } = params
            const { relativePointer, relativeFieldPointer } = relativeSelect ?? {}
            const initCondition =
                value === '' || value === undefined
                    ? []
                    : (isNumberValue(value) || isStringArray(value) ? value.toString() : '')
                          ?.split(',')
                          .filter(item => item !== CURRENT_USER.id) ?? []
            const initExpression: Conditions = reduce<string, AnyObject[]>(
                (preVal, curVal) => {
                    if (curVal === '') {
                        return preVal
                    }
                    const condition = {
                        idVariable: {
                            type: 'FIELD_ID',
                            fieldIdVariable: {
                                fieldId: relativeFieldPointer
                            }
                        },
                        operator: '=',
                        paramList: [
                            {
                                type: 'VALUE',
                                valueVariable: {
                                    value: curVal
                                }
                            }
                        ]
                    }
                    return [...preVal, condition]
                },
                [],
                initCondition
            )
            if (initExpression.length === 0) {
                return
            }
            const initFilter: FilterFormType = {
                expression: {
                    conditions: initExpression,
                    where: 'OR'
                }
            }
            if (relativePointer) {
                await getInitRecords({
                    id,
                    dsId: relativePointer,
                    pagination: { currentPage: 1, pageSize: 50 },
                    filter: initFilter
                })
            }
        },
        [getInitRecords]
    )

    const handleFetchCalendarOptions = useCallback(
        async (params: { dsId: string; filter: FilterFormType }): Promise<RecordLikeProtocol[] | undefined> => {
            const { dsId, filter } = params
            const res = await srv.getDs({
                appId,
                envId,
                dsId,
                filter,
                pagination: { currentPage: 1, pageSize: 30000 }
            })
            return res?.records
        },
        [appId, envId]
    )

    // 修改输入框配置，重新调用接口刷新
    useEffect(() => {
        newConfigList.forEach(({ config: { relativeSelect }, id }) => {
            const { relativePointer, relativeFieldPointer, relativeShowFieldPointer, filter, sorts, showMode, disabledFilter } =
                relativeSelect ?? {}
            if (relativeShowFieldPointer && relativePointer && showMode !== 'input' && relativeFieldPointer) {
                handleFetchDataSourceOptions({
                    id,
                    dsId: relativePointer,
                    fieldId: relativeFieldPointer,
                    showFieldId: relativeShowFieldPointer,
                    sorts,
                    filter: resolveFilter({
                        filter,
                        extraParams: {
                            dataSourceList,
                            parseVariable2Field
                        }
                    }),
                    disabledFilter: resolveFilter({
                        filter: disabledFilter,
                        extraParams: {
                            dataSourceList,
                            parseVariable2Field
                        }
                    }),
                    isDebounce: initLoadRef.current
                })
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSourceList, handleFetchDataSourceOptions, newConfigList])

    useUpdateEffect(() => {
        newConfigList.forEach(({ config: { relativeSelect }, id }) => {
            const { relativePointer, relativeFieldPointer, relativeShowFieldPointer, filter, sorts, showMode, disabledFilter } =
                relativeSelect ?? {}
            if (relativeShowFieldPointer && relativePointer && showMode !== 'input' && relativeFieldPointer) {
                handleFetchDataSourceOptions({
                    id,
                    dsId: relativePointer,
                    fieldId: relativeFieldPointer,
                    showFieldId: relativeShowFieldPointer,
                    sorts,
                    filter: resolveFilter({
                        filter,
                        extraParams: {
                            dataSourceList,
                            parseVariable2Field
                        }
                    }),
                    disabledFilter: resolveFilter({
                        filter: disabledFilter,
                        extraParams: {
                            dataSourceList,
                            parseVariable2Field
                        }
                    })
                })
            }
        })
    }, [parseVariable2Field])

    useEffect(() => {
        const firstConfig = newConfigList[0]
        const { relativeSelect } = firstConfig?.config ?? {}
        const { id } = firstConfig ?? {}
        const { relativePointer, relativeFieldPointer, relativeShowFieldPointer, sorts, filter, disabledFilter } = relativeSelect ?? {}
        if (!relativePointer) {
            return
        }
        const { subscribeId, unSubscribe } = pageStackPubSub.subscribe(`${relativePointer}-ADD`, (record?: RecordLikeProtocol) => {
            if (!relativePointer || !relativeFieldPointer || !relativeShowFieldPointer) {
                return
            }
            handleFetchDataSourceOptions({
                id,
                dsId: relativePointer,
                fieldId: relativeFieldPointer,
                showFieldId: relativeShowFieldPointer,
                sorts,
                filter: resolveFilter({
                    filter,
                    extraParams: {
                        dataSourceList,
                        parseVariable2Field
                    }
                }),
                disabledFilter: resolveFilter({
                    filter: disabledFilter,
                    extraParams: {
                        dataSourceList,
                        parseVariable2Field
                    }
                })
            })
        })
        return () => unSubscribe(subscribeId)
    }, [handleFetchDataSourceOptions, newConfigList, dataSourceList, parseVariable2Field])

    return useMemo(
        () => ({
            onGetInitRecord: handleGetInitRecord,
            onFetchDataSource: handleFetchDataDataSource,
            onLoadMoreData: handleLoadMoreData,
            onFetchCascadeOptions: handleFetchCascadeOptions,
            onFetchPersonOptions: handleFetchPersonOptions,
            onFetchCalendarOptions: handleFetchCalendarOptions
        }),
        [
            handleGetInitRecord,
            handleFetchDataDataSource,
            handleLoadMoreData,
            handleFetchCascadeOptions,
            handleFetchPersonOptions,
            handleFetchCalendarOptions
        ]
    )
}
